import { useSelector } from "react-redux";

import { IconButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import SuccessBannerRow from "app/pages/sales-intelligence/common-components/feature-upgrade/components/SuccessBanner/SuccessBannerRow";
import GrandAccessBanner from "app/pages/sales-intelligence/common-components/GrandAccessBanner/GrandAccessBanner";
import { COMPANY_ANALYSIS_SIGNALS } from "app/pages/sales-intelligence/constants/routes";
import { useCompanyName } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCompanyName";
import useSignalsEventsQuery from "app/pages/sales-intelligence/modules/company-analysis/queries/useSignalsEventsQuery";
import { resolvePrimarySignalHook } from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalHooks";
import type { SignalHook } from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalHooks";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import { useSignalAccessRequestSent } from "app/pages/sales-intelligence/modules/signals/hooks/useSignalAccessRequestSent";
import SignalEventBoxItem from "app/pages/sales-intelligence/pages/company-analysis/components/cards/signals-card/SignalEventBoxItem/SignalEventBoxItem";
import SignalsCardEmpty from "app/pages/sales-intelligence/pages/company-analysis/components/cards/signals-card/SignalsCardEmpty/SignalsCardEmpty";
import Card from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/Card";
import CardFooter from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/CardFooter/CardFooter";
import CardHeader from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/CardHeader/CardHeader";
import LoaderGradient from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Loader/LoaderGradient";
import { COMPANY_ANALYSIS_TAB_NAMES } from "app/pages/sales-intelligence/pages/company-analysis/constants";
import {
    SIGNALS_LIMIT,
    SIGNALS_LIMIT_WITH_UPSELL,
} from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/SignalsCard/constants";
import type { TrackingProps } from "app/pages/sales-intelligence/pages/company-analysis/containers/signals/SignalEventList/withSignalEventsTracking";
import { selectSignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import { featureHookKeys } from "app/pages/sales-intelligence/utils/i18n/feature-hook";

const SignalHookTile = ({ hook }: { hook: SignalHook }) => {
    const [isAccessRequestSent, markAccessRequestSent] = useSignalAccessRequestSent(hook.signalType);
    const isRequestSent = Boolean(hook.showsRequestSentState) && isAccessRequestSent;

    const handleRequestAccess = (): void => {
        hook.requestAccess();
        if (hook.showsRequestSentState) {
            markAccessRequestSent();
        }
    };

    return (
        <Flex
            data-automation={
                isRequestSent ? "signal-event-item-grand-access-success" : "signal-event-item-grand-access"
            }
            data-automation-type="signal-event-item-grand-access"
            flexGrow={1}
            borderRadius="6px"
            width="calc(50% - 8px)"
            maxWidth="calc(50% - 8px)"
            minHeight="168px"
        >
            {isRequestSent ? (
                <SuccessBannerRow
                    title={translate(featureHookKeys.modalSuccessTitle)}
                    subTitle={translate(featureHookKeys.modalSuccessSubtitle)}
                />
            ) : (
                <GrandAccessBanner
                    title={translate(hook.tileTitleKey)}
                    description={translate(hook.tileDescriptionKey)}
                    iconImagePath={hook.iconImagePath}
                    iconName={hook.iconName}
                    button={
                        <IconButton
                            type={"trial"}
                            onClick={handleRequestAccess}
                            iconName={"sparkles"}
                        >
                            {translate("si.common.upgrade")}
                        </IconButton>
                    }
                />
            )}
        </Flex>
    );
};

const SignalsCard = ({ onArticleLinkClick, onCompanyLinkClick, onLinkedInLinkClick }: TrackingProps) => {
    const { data, isLoading, isFetched } = useSignalsEventsQuery();
    const isEmpty = isFetched && (!data || data.length === 0);

    const companyName = useCompanyName();

    const primarySignalHook = resolvePrimarySignalHook();

    const signalLimit = primarySignalHook ? SIGNALS_LIMIT_WITH_UPSELL : SIGNALS_LIMIT;

    const signalsDescription = useSelector(selectSignalsDescription);

    const showEmptyState = isEmpty && !primarySignalHook;

    const renderContent = () => {
        return (
            <Box minHeight="364px">
                <Flex
                    justifyContent="flex-start"
                    flexWrap="wrap"
                    padding="8px 0 8px 8px"
                    gap="8px"
                >
                    {data?.slice(0, signalLimit).map((signal, index) => (
                        <SignalEventBoxItem
                            key={index}
                            signal={signal}
                            signalsDescription={signalsDescription}
                            onArticleLinkClick={onArticleLinkClick}
                            onCompanyLinkClick={onCompanyLinkClick}
                            onLinkedInLinkClick={onLinkedInLinkClick}
                        />
                    ))}
                    {primarySignalHook ? <SignalHookTile hook={primarySignalHook} /> : null}
                </Flex>
            </Box>
        );
    };

    return (
        <Card
            dataAutomation="signals-card"
            isLoading={isLoading}
            loader={
                <Box padding="8px">
                    <LoaderGradient height={344} />
                </Box>
            }
            isEmpty={showEmptyState}
            emptyState={<SignalsCardEmpty />}
            header={<CardHeader title={translate("si.company-analysis.signals.card.header")} />}
            footer={
                <CardFooter
                    stateId={COMPANY_ANALYSIS_SIGNALS}
                    linkText="si.company-analysis.signals.card.full_report"
                    isLoading={isLoading}
                    onClick={() => {
                        companyAnalysisTrackingService.widgets.trackWidgetFooterButtonClick(
                            companyName,
                            COMPANY_ANALYSIS_TAB_NAMES.signals,
                        );
                    }}
                />
            }
            renderContent={renderContent}
            width="50%"
        />
    );
};

export default SignalsCard;
